import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  GROUP_BY_PRIORITY_QUERY,
  useLazyGetStaffTasksQuery
} from "services/staff-tasks";
import type {
  StaffTask,
  StaffTasksFilter,
  StaffTasksGroupByPriorityResponse
} from "services/types/staff-tasks.type";

const LOAD_MORE_LIMIT = 25;

type ExtraTasks = Record<number, StaffTask[]>;

export default function useLoadMoreByPriority(
  groupedData: StaffTasksGroupByPriorityResponse,
  baseFilters: StaffTasksFilter
) {
  const [extraTasks, setExtraTasks] = useState<ExtraTasks>({});
  const [exhaustedPriorities, setExhaustedPriorities] = useState<Set<number>>(
    new Set()
  );
  const [loadingPriority, setLoadingPriority] = useState<number | null>(null);
  const [trigger] = useLazyGetStaffTasksQuery();

  // Reset extra tasks when the base grouped data changes (e.g. refetch)
  const prevDataRef = useRef(groupedData);
  useEffect(() => {
    if (prevDataRef.current !== groupedData) {
      prevDataRef.current = groupedData;
      setExtraTasks({});
      setExhaustedPriorities(new Set());
    }
  }, [groupedData]);

  const mergedData = useMemo((): StaffTasksGroupByPriorityResponse => {
    if (!Object.keys(extraTasks).length) return groupedData;

    const items = { ...groupedData.items };
    for (const [priorityStr, tasks] of Object.entries(extraTasks)) {
      const priority = Number(priorityStr);
      const existing = items[priority];
      if (existing) {
        const existingIds = new Set(existing.tasks.map((t) => t.id));
        const newTasks = tasks.filter((t) => !existingIds.has(t.id));
        items[priority] = {
          ...existing,
          tasks: [...existing.tasks, ...newTasks]
        };
      }
    }

    return { ...groupedData, items };
  }, [groupedData, extraTasks]);

  const loadMore = useCallback(
    async (priority: number) => {
      if (loadingPriority !== null) return;

      const group = mergedData.items[priority];
      if (
        !group ||
        group.tasks.length >= group.count ||
        exhaustedPriorities.has(priority)
      )
        return;

      setLoadingPriority(priority);

      try {
        const { group_by_priority: _, ...filtersWithoutGroupBy } = baseFilters;

        const result = await trigger({
          ...filtersWithoutGroupBy,
          query: GROUP_BY_PRIORITY_QUERY,
          priority: [priority],
          limit: LOAD_MORE_LIMIT,
          offset: group.tasks.length,
          ordering: filtersWithoutGroupBy.ordering || "due_at,-created_at"
        }).unwrap();

        if (result.length < LOAD_MORE_LIMIT) {
          setExhaustedPriorities((prev) => new Set(prev).add(priority));
        }

        setExtraTasks((prev) => ({
          ...prev,
          [priority]: [...(prev[priority] || []), ...result]
        }));
      } catch {
        // Transient errors (network, 504) — do not exhaust the priority,
        // so the user can retry by clicking "Load More" again.
      } finally {
        setLoadingPriority(null);
      }
    },
    [mergedData, baseFilters, trigger, exhaustedPriorities, loadingPriority]
  );

  const getGroupLoadMoreProps = useCallback(
    (priority: number, taskCount: number, apiCount: number) => ({
      hasMore: taskCount < apiCount && !exhaustedPriorities.has(priority),
      onLoadMore: () => loadMore(priority),
      isLoadingMore: loadingPriority === priority
    }),
    [loadMore, loadingPriority, exhaustedPriorities]
  );

  return { mergedData, getGroupLoadMoreProps };
}
